import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BASE_URL } from "../../../../components/BaseUrl";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import CustomText, { textStyles } from "../../../../components/CustomText";
import empty from "../../../../assets/empty.png";
import { useTranslation } from "react-i18next";

const WorkerReports = ({ workerId }) => {
  const { t } = useTranslation();
  const { company } = useSelector((state) => state.auth);
  const companyId = company?.id;

  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 current month range
  const getCurrentMonthRange = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const format = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;

    return {
      startDate: format(first),
      endDate: format(last),
    };
  };

  const fetchReport = async () => {
    if (!workerId || !companyId) return;

    const { startDate, endDate } = getCurrentMonthRange();

    try {
      setLoading(true);

      const res = await fetch(`${BASE_URL}/public/reports/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${decryptedToken}`,
        },
        body: JSON.stringify({
          type: "worker_daily",
          startDate,
          endDate,
          workerId,
          companyId,
          showChartData: true,
        }),
      });

      const json = await res.json();

      if (!json.error && Array.isArray(json.data?.data)) {
        const mapped = json.data.data.map((d) => ({
          date: new Date(d.report_date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          }),
          hours_worked: Number(d.hours_worked) || 0,
          tasks_completed: Number(d.tasks_completed) || 0,
          efficiency: Number(d.efficiency_percentage) || 0,
        }));

        setReportData(mapped);
      } else {
        setReportData([]);
      }
    } catch (e) {
      console.error("Worker report error:", e);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line
  }, [workerId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <CustomText
        sx={{
          ...textStyles.h1,
          fontWeight: 600,
          fontSize: "18px",
          color: "#003149",
          mb: 2,
          textAlign:"left"
        }}
      >
        {t("reportsScreen.workerDailyTitle")}
      </CustomText>

      {/* Chart */}
      <Card
        sx={{
          p: 2,
          border: "2px solid #091E4224",
          borderRadius: 2,
          boxShadow: "none",
        }}
      >
        <Typography
          sx={{
            fontSize: "15px",
            color: "#44546F",
            fontWeight: 500,
            mb: 1,
            fontFamily: "Poppins, sans-serif",
          }}
        >
          {t("reportsScreen.employee_task_day_productivity_efficiency_report")}
        </Typography>

        {reportData.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={reportData}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="hours_worked"
                stroke="#1976d2"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="tasks_completed"
                stroke="#66BB6A"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="efficiency"
                stroke="#FFA726"
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Box
            height={320}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <img src={empty} alt="No data" style={{ width: 180, opacity: 0.7 }} />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default WorkerReports;
